/**
 * InfinitSales - Saved Messages Page
 *
 * CRUD-backed list of user-registered "knowledge base" messages stored in the
 * Azure Cosmos container `SavedMessages`.
 *
 * Each saved message supports:
 *   - title + text (manual)
 *   - audio (recorded inline, optionally transcribed via /api/audio/transcribe)
 *   - multiple file attachments (uploaded via /api/files/upload)
 *
 * Uploads reuse fileUploadService (same service used by the chat attachment
 * flow), so no backend changes are needed for file/audio storage.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  History,
  Plus,
  Trash2,
  Pencil,
  Mic,
  Square,
  Play,
  Pause,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  File as FileIcon,
  X,
  Loader2,
  Sparkles,
  AlertCircle,
  Save,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import NavSidebar from '../../components/NavSidebar';
import savedMessagesService from '../../services/global/savedMessagesService';
import fileUploadService from '../../services/global/fileUploadService';
import api from '../../services/global/api';
import VariableChips from '../../components/messages/VariableChips';

const FILE_TYPE_ICONS = {
  pdf: FileText,
  image: ImageIcon,
  video: Video,
  audio: Music,
  document: FileText,
  file: FileIcon,
};

const FILE_TYPE_COLORS = {
  pdf: '#E53935',
  image: '#43A047',
  video: '#1E88E5',
  audio: '#8E24AA',
  document: '#FB8C00',
  file: '#757575',
};

const inferType = (attachment = {}) => {
  if (attachment.type) return attachment.type;
  const mime = (attachment.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('application/') || mime.startsWith('text/')) return 'document';
  return 'file';
};

const SavedMessagesPage = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user } = useAuth();

  // Saved messages (server)
  const [savedMessages, setSavedMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // ============================================
  // DATA LOAD
  // ============================================

  const loadSaved = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await savedMessagesService.list();
      setSavedMessages(res?.items || []);
    } catch (err) {
      console.error('[SavedMessagesPage] list error:', err);
      setError(err.message || 'Erro ao carregar mensagens salvas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSaved();
  }, [loadSaved]);

  // ============================================
  // NAVIGATION
  // ============================================

  const isBusinessChannel = user?.default_channel === 'wa_business';

  const handlePageChange = (page) => {
    if (page === 'saved-messages') return;
    // Paginas standalone (com rota propria) navegam direto entre si — sem
    // passar pelo Inbox como intermediario. Senao o Inbox usa setCurrentPage
    // do useEffect de targetPage e renderiza o default do switch (a propria
    // Inbox), causando o flicker "abre Inbox antes da pagina certa".
    const standaloneRoutes = {
      'meta-leads': '/meta-leads',
      'saved-messages': '/saved-messages',
      'campaigns': '/campaigns',
      'empresa': '/empresa',
    };
    if (standaloneRoutes[page]) {
      navigate(standaloneRoutes[page]);
      return;
    }
    navigate(isBusinessChannel ? '/wa-business' : '/inbox', { state: { targetPage: page } });
  };

  // ============================================
  // SAVED MESSAGE ACTIONS
  // ============================================

  const handleNew = () => {
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir a mensagem "${item.title}"?`)) return;
    try {
      await savedMessagesService.remove(item.id);
      setSavedMessages((prev) => prev.filter((m) => m.id !== item.id));
    } catch (err) {
      alert(err.message || 'Erro ao excluir mensagem');
    }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        const updated = await savedMessagesService.update(editing.id, payload);
        setSavedMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      } else {
        const created = await savedMessagesService.create(payload);
        setSavedMessages((prev) => [created, ...prev]);
      }
      setShowModal(false);
      setEditing(null);
    } catch (err) {
      alert(err.message || 'Erro ao salvar mensagem');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // STYLES
  // ============================================

  const s = useMemo(() => ({
    container: {
      display: 'flex',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    mainArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 24px',
      backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm,
      minHeight: 60,
      zIndex: 1,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
    headerIcon: {
      width: 40, height: 40,
      backgroundColor: colors.accentPrimary,
      borderRadius: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white',
    },
    headerTitle: { fontSize: 18, fontWeight: 600, color: colors.textPrimary, margin: 0 },
    headerSubtitle: { fontSize: 13, color: colors.textSecondary, margin: 0 },
    tabBar: {
      display: 'flex',
      gap: 0,
      backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm,
      padding: '0 24px',
      zIndex: 1,
    },
    tab: {
      padding: '12px 20px',
      fontSize: 13,
      fontWeight: 500,
      color: colors.textSecondary,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      position: 'relative',
      marginBottom: -1,
    },
    tabActive: {
      color: colors.accentPrimary,
      fontWeight: 600,
      boxShadow: `inset 0 -2px 0 ${colors.accentPrimary}`,
    },
    content: { flex: 1, overflow: 'auto', padding: '20px 24px' },
    toolbar: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: 16, gap: 12,
    },
    btn: {
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '8px 16px',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 8,
      backgroundColor: 'transparent',
      color: colors.textPrimary,
      fontSize: 13, fontWeight: 500,
      cursor: 'pointer',
    },
    btnPrimary: {
      backgroundColor: colors.accentPrimary,
      color: '#fff',
      border: 'none',
    },
    btnDanger: {
      color: '#ef4444',
      borderColor: '#ef444440',
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: 16,
    },
    card: {
      backgroundColor: colors.bgSecondary,
      borderRadius: 12,
      border: `1px solid ${colors.borderColor}`,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    cardTitle: {
      fontSize: 15, fontWeight: 600,
      color: colors.textPrimary,
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      flex: 1,
    },
    cardText: {
      fontSize: 13,
      color: colors.textSecondary,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxHeight: 80,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      margin: 0,
    },
    cardMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      fontSize: 11,
      color: colors.textTertiary,
      flexWrap: 'wrap',
    },
    metaChip: {
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '2px 8px',
      backgroundColor: colors.bgTertiary,
      borderRadius: 999,
    },
    cardActions: {
      display: 'flex', gap: 6, justifyContent: 'flex-end',
    },
    iconBtn: {
      padding: 6,
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      cursor: 'pointer',
      borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    empty: {
      padding: 40,
      textAlign: 'center',
      color: colors.textSecondary,
      fontSize: 14,
    },
    errorBox: {
      backgroundColor: '#ef444415',
      color: '#ef4444',
      padding: 12,
      borderRadius: 8,
      marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 8,
      fontSize: 13,
    },
    loadingCenter: {
      display: 'flex', justifyContent: 'center', padding: 40,
      color: colors.textSecondary,
    },
  }), [colors]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div style={s.container}>
      <NavSidebar currentPage="saved-messages" onPageChange={handlePageChange} />

      <div style={s.mainArea}>
        <div style={s.header}>
          <div style={s.headerLeft}>
            <div style={s.headerIcon}>
              <History size={20} />
            </div>
            <div>
              <h1 style={s.headerTitle}>Mensagens Prontas</h1>
              <p style={s.headerSubtitle}>
                Mantenha uma base de respostas prontas para reutilizar nas conversas
              </p>
            </div>
          </div>
          <button
            style={{ ...s.btn, ...s.btnPrimary }}
            onClick={handleNew}
          >
            <Plus size={16} /> Nova Mensagem
          </button>
        </div>

        <div style={s.content}>
              {error && (
                <div style={s.errorBox}>
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              {loading ? (
                <div style={s.loadingCenter}>
                  <Loader2 size={24} className="spin-icon" />
                </div>
              ) : savedMessages.length === 0 ? (
                <div style={s.empty}>
                  Nenhuma mensagem salva ainda. Clique em "Nova Mensagem" para começar.
                </div>
              ) : (
                <div style={s.cardGrid}>
                  {savedMessages.map((item) => {
                    const attachCount = item.attachments?.length || 0;
                    const hasAudio = !!item.audio?.file_url;
                    return (
                      <div key={item.id} style={s.card}>
                        <div style={s.cardHeader}>
                          <div style={s.cardTitle}>{item.title}</div>
                        </div>
                        {item.text && <p style={s.cardText}>{item.text}</p>}
                        <div style={s.cardMeta}>
                          {hasAudio && (
                            <span style={s.metaChip}>
                              <Mic size={12} /> Áudio
                            </span>
                          )}
                          {attachCount > 0 && (
                            <span style={s.metaChip}>
                              <Paperclip size={12} /> {attachCount} anexo{attachCount > 1 ? 's' : ''}
                            </span>
                          )}
                          {!hasAudio && attachCount === 0 && (
                            <span style={{ ...s.metaChip, opacity: 0.6 }}>
                              Somente texto
                            </span>
                          )}
                        </div>
                        <div style={s.cardActions}>
                          <button
                            style={s.iconBtn}
                            onClick={() => handleEdit(item)}
                            title="Editar"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            style={{ ...s.iconBtn, color: '#ef4444' }}
                            onClick={() => handleDelete(item)}
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
        </div>
      </div>

      {showModal && (
        <SavedMessageModal
          initial={editing}
          onCancel={() => { setShowModal(false); setEditing(null); }}
          onSave={handleSave}
          saving={saving}
          colors={colors}
        />
      )}

      <style>{`
        .spin-icon { animation: smp-spin 1s linear infinite; }
        @keyframes smp-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// SavedMessageModal — create/edit UI
// ============================================================

const SavedMessageModal = ({ initial, onCancel, onSave, saving, colors }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [text, setText] = useState(initial?.text || '');
  const [attachments, setAttachments] = useState(initial?.attachments || []);
  const [audio, setAudio] = useState(initial?.audio || null);

  // Upload state
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Audio recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null); // Blob pending save/upload
  const [audioBlobUrl, setAudioBlobUrl] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup object URL on unmount
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [audioBlobUrl]);

  // ---------- ATTACHMENTS ----------
  const handlePickFiles = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadError(null);
    setUploadingAttachment(true);
    try {
      for (const file of files) {
        const validation = fileUploadService.validateFile(file);
        if (!validation.valid) {
          setUploadError(validation.error);
          continue;
        }
        // fileUploadService returns { url, filename, type, mimeType, size } (camelCase)
        const uploaded = await fileUploadService.uploadFile(file);
        if (uploaded?.url) {
          setAttachments((prev) => [
            ...prev,
            {
              file_url: uploaded.url,
              file_name: uploaded.filename || file.name,
              mime_type: uploaded.mimeType || file.type,
              type: uploaded.type || inferType({ mime_type: file.type }),
              file_size: uploaded.size || file.size,
            },
          ]);
        }
      }
    } catch (err) {
      setUploadError(err.message || 'Erro ao enviar arquivo');
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- AUDIO ----------
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
        setAudioBlobUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();

      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(
        () => setRecordingSeconds((s) => s + 1),
        1000,
      );
    } catch (err) {
      alert('Não foi possível acessar o microfone. Verifique as permissões.');
      console.error(err);
    }
  };

  const stopRecording = () => {
    try {
      mediaRecorderRef.current?.stop();
    } catch (err) {
      console.error(err);
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const discardRecording = () => {
    if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
    setAudioBlob(null);
    setAudioBlobUrl(null);
  };

  const transcribeRecording = async () => {
    if (!audioBlob) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append('file', new File([audioBlob], 'audio.webm', { type: 'audio/webm' }));
      const res = await api.post('/api/audio/transcribe', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const transcript = res?.data?.data?.transcript || '';
      if (transcript) {
        setText((prev) => (prev ? `${prev}\n${transcript}` : transcript));
      }
    } catch (err) {
      alert(err?.response?.data?.detail || err.message || 'Erro ao transcrever áudio');
    } finally {
      setTranscribing(false);
    }
  };

  const attachRecordedAudio = async () => {
    if (!audioBlob) return;
    setUploadingAudio(true);
    try {
      const file = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
      // fileUploadService returns { url, filename, type, mimeType, size }
      const uploaded = await fileUploadService.uploadFile(file);
      if (uploaded?.url) {
        setAudio({
          file_url: uploaded.url,
          file_name: uploaded.filename || file.name,
          duration: recordingSeconds,
          mime_type: uploaded.mimeType || 'audio/webm',
        });
        discardRecording();
      }
    } catch (err) {
      alert(err.message || 'Erro ao enviar áudio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const removeSavedAudio = () => setAudio(null);

  // ---------- SUBMIT ----------
  const canSave = title.trim().length > 0 && !saving;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;
    onSave({
      title: title.trim(),
      text: text.trim(),
      attachments,
      audio,
    });
  };

  // ---------- STYLES ----------
  const m = {
    backdrop: {
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
      padding: 16,
    },
    dialog: {
      width: '100%', maxWidth: 640,
      maxHeight: '90vh',
      backgroundColor: colors.bgPrimary,
      borderRadius: 14,
      border: `1px solid ${colors.borderColor}`,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    },
    header: {
      padding: '14px 20px',
      borderBottom: `1px solid ${colors.borderColor}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    title: { fontSize: 16, fontWeight: 600, color: colors.textPrimary, margin: 0 },
    body: { padding: 20, overflowY: 'auto', flex: 1 },
    field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
    label: { fontSize: 12, fontWeight: 600, color: colors.textSecondary },
    input: {
      padding: '10px 12px',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 8,
      backgroundColor: colors.bgSecondary,
      color: colors.textPrimary,
      fontSize: 14,
      outline: 'none',
      fontFamily: 'inherit',
    },
    textarea: {
      padding: '10px 12px',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 8,
      backgroundColor: colors.bgSecondary,
      color: colors.textPrimary,
      fontSize: 14,
      outline: 'none',
      fontFamily: 'inherit',
      minHeight: 100,
      resize: 'vertical',
    },
    inlineRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
    btn: {
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '8px 14px',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 8,
      backgroundColor: colors.bgSecondary,
      color: colors.textPrimary,
      fontSize: 13, fontWeight: 500,
      cursor: 'pointer',
    },
    btnPrimary: {
      backgroundColor: colors.accentPrimary,
      color: '#fff',
      border: 'none',
    },
    btnDanger: {
      borderColor: '#ef444440',
      color: '#ef4444',
    },
    btnDisabled: {
      opacity: 0.5, cursor: 'not-allowed',
    },
    attachList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 },
    attachItem: {
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px',
      borderRadius: 8,
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.borderColor}`,
    },
    audioBox: {
      padding: 12,
      borderRadius: 10,
      backgroundColor: colors.bgSecondary,
      border: `1px solid ${colors.borderColor}`,
    },
    recordingDot: {
      width: 10, height: 10, borderRadius: '50%',
      backgroundColor: '#ef4444',
      animation: 'smp-pulse 1s infinite',
    },
    footer: {
      padding: 14,
      borderTop: `1px solid ${colors.borderColor}`,
      display: 'flex', justifyContent: 'flex-end', gap: 8,
    },
  };

  return (
    <div style={m.backdrop} onClick={onCancel}>
      <div style={m.dialog} onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <div style={m.header}>
            <h3 style={m.title}>
              {initial ? 'Editar Mensagem Salva' : 'Nova Mensagem Salva'}
            </h3>
            <button
              type="button"
              style={{ ...m.btn, padding: 6, border: 'none', background: 'transparent' }}
              onClick={onCancel}
              title="Fechar"
            >
              <X size={18} />
            </button>
          </div>

          <div style={m.body}>
            <div style={m.field}>
              <label style={m.label}>Título *</label>
              <input
                style={m.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Horário de funcionamento"
                maxLength={200}
                required
              />
            </div>

            <div style={m.field}>
              <label style={m.label}>Texto da mensagem</label>
              <textarea
                style={m.textarea}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Escreva a resposta ou clique em Gravar + Transcrever para preencher por voz."
                maxLength={10000}
              />
              <VariableChips
                onInsert={(token) =>
                  setText((prev) => (prev ? `${prev}${prev.endsWith(' ') || prev.endsWith('\n') ? '' : ' '}${token}` : token))
                }
              />
            </div>

            {/* Audio */}
            <div style={m.field}>
              <label style={m.label}>Áudio (opcional)</label>
              <div style={m.audioBox}>
                {audio?.file_url ? (
                  <div style={m.inlineRow}>
                    <Music size={16} color={FILE_TYPE_COLORS.audio} />
                    <audio controls src={audio.file_url} style={{ flex: 1 }} />
                    <button
                      type="button"
                      style={{ ...m.btn, ...m.btnDanger }}
                      onClick={removeSavedAudio}
                    >
                      <Trash2 size={14} /> Remover
                    </button>
                  </div>
                ) : audioBlobUrl ? (
                  <>
                    <div style={m.inlineRow}>
                      <audio controls src={audioBlobUrl} style={{ flex: 1 }} />
                    </div>
                    <div style={{ ...m.inlineRow, marginTop: 8 }}>
                      <button
                        type="button"
                        style={{
                          ...m.btn,
                          ...(transcribing ? m.btnDisabled : {}),
                        }}
                        onClick={transcribeRecording}
                        disabled={transcribing}
                      >
                        {transcribing
                          ? <Loader2 size={14} className="spin-icon" />
                          : <Sparkles size={14} />}
                        {transcribing ? 'Transcrevendo…' : 'Transcrever para texto'}
                      </button>
                      <button
                        type="button"
                        style={{
                          ...m.btn, ...m.btnPrimary,
                          ...(uploadingAudio ? m.btnDisabled : {}),
                        }}
                        onClick={attachRecordedAudio}
                        disabled={uploadingAudio}
                      >
                        {uploadingAudio
                          ? <Loader2 size={14} className="spin-icon" />
                          : <Save size={14} />}
                        {uploadingAudio ? 'Enviando…' : 'Anexar como áudio'}
                      </button>
                      <button
                        type="button"
                        style={{ ...m.btn, ...m.btnDanger }}
                        onClick={discardRecording}
                      >
                        <X size={14} /> Descartar
                      </button>
                    </div>
                  </>
                ) : isRecording ? (
                  <div style={m.inlineRow}>
                    <div style={m.recordingDot} />
                    <span style={{ color: colors.textPrimary, fontWeight: 500 }}>
                      Gravando... {Math.floor(recordingSeconds / 60)}:
                      {String(recordingSeconds % 60).padStart(2, '0')}
                    </span>
                    <div style={{ flex: 1 }} />
                    <button
                      type="button"
                      style={{ ...m.btn, ...m.btnDanger }}
                      onClick={stopRecording}
                    >
                      <Square size={14} /> Parar
                    </button>
                  </div>
                ) : (
                  <div style={m.inlineRow}>
                    <span style={{ color: colors.textSecondary, fontSize: 13 }}>
                      Grave um áudio para anexar à mensagem ou transcrevê-lo em texto.
                    </span>
                    <div style={{ flex: 1 }} />
                    <button
                      type="button"
                      style={{ ...m.btn, ...m.btnPrimary }}
                      onClick={startRecording}
                    >
                      <Mic size={14} /> Gravar áudio
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div style={m.field}>
              <label style={m.label}>Anexos</label>
              <div style={m.inlineRow}>
                <button
                  type="button"
                  style={{
                    ...m.btn,
                    ...(uploadingAttachment ? m.btnDisabled : {}),
                  }}
                  onClick={handlePickFiles}
                  disabled={uploadingAttachment}
                >
                  {uploadingAttachment
                    ? <Loader2 size={14} className="spin-icon" />
                    : <Paperclip size={14} />}
                  {uploadingAttachment ? 'Enviando…' : 'Anexar arquivo'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {uploadError && (
                  <span style={{ color: '#ef4444', fontSize: 12 }}>
                    {uploadError}
                  </span>
                )}
              </div>

              {attachments.length > 0 && (
                <div style={m.attachList}>
                  {attachments.map((att, i) => {
                    const type = inferType(att);
                    const Icon = FILE_TYPE_ICONS[type] || FileIcon;
                    const color = FILE_TYPE_COLORS[type] || '#757575';
                    return (
                      <div key={i} style={m.attachItem}>
                        <Icon size={16} color={color} />
                        <span style={{
                          flex: 1,
                          color: colors.textPrimary,
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {att.file_name}
                        </span>
                        <button
                          type="button"
                          style={{
                            ...m.btn,
                            padding: 4,
                            border: 'none',
                            background: 'transparent',
                            color: '#ef4444',
                          }}
                          onClick={() => removeAttachment(i)}
                          title="Remover"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={m.footer}>
            <button type="button" style={m.btn} onClick={onCancel}>
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                ...m.btn, ...m.btnPrimary,
                ...(canSave ? {} : m.btnDisabled),
              }}
              disabled={!canSave}
            >
              {saving
                ? <Loader2 size={14} className="spin-icon" />
                : <Save size={14} />}
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes smp-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default SavedMessagesPage;
